import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { MAIN_URL } from "../../../Configurations/Urls";
import { convertInternToEmployee } from "../../../Apis/InternManagement";
import { fetchOrganizationEmploymentTypes } from "../../../Apis/Organization-Employement-types";
import { fetchOrganizationEmployee } from "../../../Apis/Employee-api";

const emptyForm = {
  employee_code: "",
  organization_designation_id: "",
  organization_employment_type_id: "",
  organization_employment_stage_id: "",
  organization_employment_status_id: "",
  date_of_joining: null,
  reporting_manager_id: "",
  organization_unit_id: "",
  organization_department_location_id: "",
  organization_work_shift_id: "",
};

function InternConvertDialog({ open, onClose, internRow, orgId, onConverted }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [designations, setDesignations] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [employmentStages, setEmploymentStages] = useState([]);
  const [managers, setManagers] = useState([]);
  const [internName, setInternName] = useState("");

  const orgPersonId = internRow?.organization_person_id ?? internRow?.id;

  const personLabel = useMemo(() => {
    return (
      internName ||
      internRow?.name ||
      internRow?.full_name ||
      internRow?.intern_code ||
      "Intern"
    );
  }, [internName, internRow]);

  useEffect(() => {
    if (!open || !orgId || !orgPersonId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrors({});
      try {
        const [internRes, typesRes, stagesRes, empRes, desigRes] =
          await Promise.all([
            axios.get(
              `${MAIN_URL}/api/organizations/${orgId}/interns/${orgPersonId}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            ),
            fetchOrganizationEmploymentTypes(orgId),
            axios.get(
              `${MAIN_URL}/api/organizations/${orgId}/employment-stages?per_page=all`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            ),
            fetchOrganizationEmployee(orgId),
            axios.get(`${MAIN_URL}/api/organizations/${orgId}/designation`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }),
          ]);

        if (cancelled) return;

        const intern = internRes?.data?.intership || {};
        const person = intern?.OrgPersons?.Person || intern?.org_persons?.person || {};
        const first = person.first_name || intern.first_name || "";
        const last = person.last_name || intern.last_name || "";
        setInternName(
          [first, last].filter(Boolean).join(" ") ||
            internRow?.name ||
            intern.intern_code ||
            ""
        );

        setForm({
          ...emptyForm,
          employee_code: intern.intern_code || internRow?.intern_code || "",
          organization_unit_id: intern.organization_unit_id || "",
          organization_department_location_id:
            intern.organization_department_location_id || "",
          organization_work_shift_id: intern.organization_work_shift_id || "",
          reporting_manager_id: intern.mentor_organization_person_id || "",
          date_of_joining: intern.internship_end_date
            ? new Date(intern.internship_end_date)
            : new Date(),
        });

        setEmploymentTypes(
          Array.isArray(typesRes?.employemtType?.data)
            ? typesRes.employemtType.data
            : Array.isArray(typesRes?.organizationemploymenttype)
              ? typesRes.organizationemploymenttype
              : Array.isArray(typesRes?.EmploymentTypes)
                ? typesRes.EmploymentTypes
                : Array.isArray(typesRes?.data)
                  ? typesRes.data
                  : []
        );

        // Same shape as employee form store: response.data.stages.data
        const stagesPayload = stagesRes?.data?.stages ?? stagesRes?.stages;
        const stagesList = Array.isArray(stagesPayload?.data)
          ? stagesPayload.data
          : Array.isArray(stagesPayload)
            ? stagesPayload
            : [];
        setEmploymentStages(stagesList);

        const employeeList = Array.isArray(empRes)
          ? empRes
          : empRes?.Employees || empRes?.employees || empRes?.data || [];
        setManagers(Array.isArray(employeeList) ? employeeList : []);
        const desigList =
          desigRes?.data?.organizationdesignation ||
          desigRes?.data?.designations ||
          desigRes?.data?.data ||
          [];
        setDesignations(Array.isArray(desigList) ? desigList : []);
      } catch (error) {
        console.error(error);
        toast.error(error.message || "Failed to load conversion data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, orgPersonId, internRow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "organization_employment_stage_id") {
      const stage = employmentStages.find(
        (s) => String(s.organization_employment_stage_id) === String(value)
      );
      setForm((prev) => ({
        ...prev,
        organization_employment_stage_id: value,
        organization_employment_status_id:
          stage?.organization_employment_status_id || "",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!orgId || !orgPersonId) return;
    const nextErrors = {};
    if (!String(form.employee_code || "").trim()) {
      nextErrors.employee_code = "Employee code is required";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    try {
      const payload = {
        employee_code: String(form.employee_code).trim(),
        organization_designation_id: form.organization_designation_id || null,
        organization_employment_type_id:
          form.organization_employment_type_id || null,
        organization_employment_stage_id:
          form.organization_employment_stage_id || null,
        organization_employment_status_id:
          form.organization_employment_status_id || null,
        organization_unit_id: form.organization_unit_id || null,
        organization_department_location_id:
          form.organization_department_location_id || null,
        organization_work_shift_id: form.organization_work_shift_id || null,
        reporting_manager_id: form.reporting_manager_id || null,
        date_of_joining: form.date_of_joining
          ? new Date(form.date_of_joining).toISOString().slice(0, 10)
          : null,
      };

      const result = await convertInternToEmployee(orgId, orgPersonId, payload);
      toast.success(result.message || "Intern converted to employee");
      onConverted?.(result);
      onClose?.();
      navigate(
        `/organization/employee/employee-details/view/${orgPersonId}`
      );
    } catch (error) {
      toast.error(error.message || "Conversion failed");
    } finally {
      setSubmitting(false);
    }
  };

  const ensureArray = (value) => (Array.isArray(value) ? value : []);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Convert Intern to Employee</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Convert {personLabel} to an employee. Profile, education,
              languages, family, addresses, contacts, experiences, and medical
              stay with this person. Payment methods and documents are copied.
              Attendance and leaves are not converted.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                required
                label="Employee Code"
                name="employee_code"
                value={form.employee_code}
                onChange={handleChange}
                error={!!errors.employee_code}
                helperText={errors.employee_code}
                inputProps={{ maxLength: 10 }}
              />

              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={enGB}
              >
                <DatePicker
                  label="Date of Joining"
                  value={form.date_of_joining}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, date_of_joining: value }))
                  }
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                select
                fullWidth
                label="Designation"
                name="organization_designation_id"
                value={form.organization_designation_id}
                onChange={handleChange}
              >
                <MenuItem value="">Select</MenuItem>
                {ensureArray(designations).map((d) => (
                  <MenuItem
                    key={d.organization_designation_id}
                    value={d.organization_designation_id}
                  >
                    {d.designation_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Employment Type"
                name="organization_employment_type_id"
                value={form.organization_employment_type_id}
                onChange={handleChange}
              >
                <MenuItem value="">Select</MenuItem>
                {ensureArray(employmentTypes).map((t) => (
                  <MenuItem
                    key={t.organization_employment_type_id}
                    value={t.organization_employment_type_id}
                  >
                    {t.employment_type_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Employment Stage"
                name="organization_employment_stage_id"
                value={form.organization_employment_stage_id}
                onChange={handleChange}
                disabled={ensureArray(employmentStages).length === 0}
              >
                <MenuItem value="">Select</MenuItem>
                {ensureArray(employmentStages).map((s) => (
                  <MenuItem
                    key={s.organization_employment_stage_id}
                    value={s.organization_employment_stage_id}
                  >
                    {s.employment_stage_name || s.stage_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Reporting Manager"
                name="reporting_manager_id"
                value={form.reporting_manager_id}
                onChange={handleChange}
                sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}
              >
                <MenuItem value="">Select</MenuItem>
                {ensureArray(managers).map((m) => {
                  const id = m.organization_person_id || m.employee_id || m.id;
                  const code =
                    m.employee_code || m.person_code || m.intern_code || "";
                  const name =
                    m.full_name ||
                    m.name ||
                    [m.first_name, m.last_name].filter(Boolean).join(" ") ||
                    code;
                  return (
                    <MenuItem key={id} value={id}>
                      {name}
                      {code ? ` (${code})` : ""}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || submitting}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Convert"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InternConvertDialog;
